import { nothing } from 'lit-html';
import { html } from 'lit-element';
import debounce from 'lodash/debounce';
import { ifNotNull } from '../../utils/directives';
import { checkSlots, cleanDefaultSlot } from '../../shared/slot-utils';
import { KatLitElement, KatalComponent, Keys } from '../../shared/base';
import { nextUniqueId } from '../../shared/base/unique-id';
import {
  getFirstMatchingParent,
  getContentOfFirstTextNode,
} from '../../shared/utils';
import baseStyles from '../../shared/base/base.lit.scss';
import formItemStyles from '../../shared/base/form-item.base.lit.scss';
import styles from './dropdown.lit.scss';
import { fitHandler } from './fit-handler';
import strings from './strings';

/**
 * @component {kat-dropdown} KatalDropdown A dropdown is a form element that allows the selection of a single choice from a list of items.
 * @subcomponent ./option.js
 * @guideline Do Stringify data object when passing it to attribute data
 * @guideline Do If possible, pre-select a good default item from the list to help the customer make an informed decision——similar to having a good default input value in an input field.
 * @guideline Do If possible, only include valid options in a dropdown. A user should typically not be able to choose an option that is invalid.
 * @guideline Dont Do not use the descriptions under the labels for each item unless absolutely necessary.
 * @contentProvided
 * @example Basic {"label": "Basic Dropdown", "value":"testValue2", "options": [{"name":"Value1","value":"testValue","icon":"optional-icon-name"},{"name":"Value2","value":"testValue2","icon":"optional-icon-name-2"},{"name":"disabledValue","value":"disabledValue","icon":"optional-icon-name", "disabled": true}]}
 * @example Large {"value":"", "options": [{"name":"car","value":"carValue"},{"name":"truck","value":"truckValue"},{"name":"house","value":"houseValue"},{"name":"plane","value":"planeValue"},{"name":"cat","value":"catValue"},{"name":"dog","value":"dogValue"},{"name":"fish","value":"fishValue"},{"name":"bird","value":"birdValue"},{"name":"lizard","value":"lizardValue"},{"name":"turtle","value":"turtleValue"},{"name":"bug","value":"bugValue"}]}
 * @example Countries {"value": "", "content": "
 *   <kat-option value=\"ca\">
 *     <kat-icon name=\"flag-ca\" size=\"small\"></kat-icon>
 *     <span>Canada</span>
 *   </kat-option>
 *   <kat-option value=\"de\">
 *     <kat-icon name=\"flag-de\" size=\"small\"></kat-icon>
 *     <span>Germany</span>
 *   </kat-option>
 *   <kat-option value=\"it\">
 *     <kat-icon name=\"flag-it\" size=\"small\"></kat-icon>
 *     <span>Italy</span>
 *   </kat-option>
 *   <kat-option value=\"us\">
 *     <kat-icon name=\"flag-us\" size=\"small\"></kat-icon>
 *     <span>United States</span>
 *   </kat-option>
 * "}
 * @example Error {"value": "", "placeholder": "Pick a topping", "state": "error", "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @example WithLabels {"value": "", "placeholder": "Pick a topping", "state-label": "Error with selection",
 * "state-emphasis": "Error", "label": "Dropdown with labels", "constraint-emphasis": "Note", "constraint-label": "This is a constraint label",
 * "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @example WithTooltip {"label": "Dropdown with tooltip", "tooltip-text": "Hello world",
 * "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @event {{ value: string }} change This event is fired whenever the dropdown is finished updating
 * @event {} expand This event is fired whenever the dropdown is expanded.
 * @event {} collapse This event is fired whenever the dropdown is collapsed.
 * @status Production
 * @theme flo
 */
export class KatalDropdown extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {json} options Array of `Option` objects, e.g. [{"name":"test","value":"testValue","icon":"optional-icon-name"}]
       * @jsonExample - {"options": "[{"name":"test","value":"testValue","icon":"optional-icon-name"}]"}
       * @required
       */
      options: { type: Array },

      /**
       * @classprop {string} placeholder The text that will be displayed to the user as a placeholder.
       */
      placeholder: { type: String, reflect: true },

      /**
       * @classprop {string} max-height The max height of the selection area.
       * This should be a css value with units, e.g. "8px".
       */
      maxHeight: { type: String, attribute: 'max-height', reflect: true },

      /**
       * @classprop {string} name The optional field name, hidden from view.
       */
      name: { type: String, reflect: true },

      /**
       * @classprop {boolean} expanded Whether the component is currently showing its dropdown options.
       */
      expanded: { type: Boolean, reflect: true },

      /**
       * @classprop {string} value The value of the selected option.
       */
      value: { type: String, reflect: true },

      /**
       * @classprop {enum} size The size of the selected option.
       * @enum {value} large Large select box - Default
       * @enum {value} small Small select box
       */
      size: { type: String, reflect: true },

      /**
       * @classprop {boolean} disabled Setting the select box to disabled will stop its interactivity.
       */
      disabled: { type: Boolean, reflect: true },

      /**
       * @classprop {string} kat-aria-label The label of the dropdown visible only to screenreaders.
       */
      katAriaLabel: { type: String, reflect: true },

      /**
       * @classprop {string} label The optional field label.
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} constraint-emphasis Optional pretext for the constraint label.  Used to
       * provide addtional context to the constraint for the input
       */
      constraintEmphasis: {
        type: String,
        attribute: 'constraint-emphasis',
        reflect: true,
      },

      /**
       * @classprop {string} constraint-label Provides users with more information about what they enter into the input
       */
      constraintLabel: {
        type: String,
        attribute: 'constraint-label',
        reflect: true,
      },

      /**
       * @classprop {enum} state Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
       * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
       */
      state: { type: String, reflect: true },

      /**
       * @classprop {string} state-emphasis Optional pretext for the state label. Provides additional context to the state for the input.
       */
      stateEmphasis: {
        type: String,
        attribute: 'state-emphasis',
        reflect: true,
      },

      /**
       * @classprop {string} state-label Provides users with more information about why the input is in the state it is in
       */
      stateLabel: { type: String, attribute: 'state-label', reflect: true },

      /**
       * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
       * set.
       */
      tooltipText: { type: String, attribute: 'tooltip-text', reflect: true },

      /**
       * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
       */
      tooltipPosition: {
        type: String,
        attribute: 'tooltip-position',
        reflect: true,
      },

      /**
       * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
       */
      tooltipTriggerIcon: {
        type: String,
        attribute: 'tooltip-trigger-icon',
        reflect: true,
      },

      /**
       * @classprop {boolean} searchable Setting the select box to searchable will add a filter box to refine the options displayed
       */
      searchable: { type: Boolean, reflect: true },

      // Needs to be in the property list so that changing it will re-render the component
      _searchTerm: { attribute: false },

      /**
       * @classprop {string} locale Locale for the "no options" text in search bar
       */
      locale: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [baseStyles, formItemStyles, styles];
  }

  _propsToReflectToHiddenInput = ['name', 'value', 'disabled'];

  constructor() {
    super();

    this._firstTimeRenderingOptions = true;
    this._jumpToSearch = '';
    this._timeoutId = 0;
    this._uniqueId = nextUniqueId();

    this.options = [];
    this.size = 'large';
    this.locale = 'en-US';

    cleanDefaultSlot(this);
    this.observeChildren(this, () => this.requestUpdate());
    this._allOptionsAreHidden = false; // Trigger for "no options" message

    this.onInputDebounced = debounce(this.onInputDebounced.bind(this), 150);
  }

  firstUpdated() {
    this.createEvents();
  }

  // Only used for searchable drop-downs; this responds to the input field
  onInput(evt) {
    this.onInputDebounced(evt.target.value);
  }

  // Deferred call, due to constructor redefinition
  onInputDebounced(searchTerm) {
    this._searchTerm = searchTerm;

    if (this._hasSlottedOptions()) {
      this.toggleElementVisibility();
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('expanded')) {
      this.setOrUnsetOptionsContainerBottomOffset();
    }

    if (changedProperties.has('value')) {
      const options = Array.from(this.children).filter(
        c => c.tagName === 'KAT-OPTION'
      );
      options.forEach(opt => {
        if (opt.selected) opt.selected = false;
        if (opt.value === this.value) opt.selected = true;
      });
    }
  }

  renderOptions() {
    // We want to defer rendering options until the dropdown is expanded.
    // There are issues with rendering fallback content into a defaul slot if it was initially
    // rendered blank (with say `nothing`). To resolve this we defer rendering the slot element
    // until some content ready to render. So this function will return undefined until it's ready
    // to render something. This way render() knows when it's okay to render the slot.
    if (this.expanded || !this._firstTimeRenderingOptions) {
      const theSearchTerm = this._searchTerm;
      const isSearchable = this.searchable;

      this._firstTimeRenderingOptions = false;

      const ret = this.options.reduce((elements, option, index) => {
        const { name, value, icon, disabled } = option;

        if (
          isSearchable &&
          theSearchTerm &&
          !name.toLowerCase().includes(theSearchTerm.toLowerCase())
        ) {
          return elements;
        }

        const selected = value === this.value ? 'selected' : '';

        const iconElement = icon
          ? html`<kat-icon name="${icon}" size="tiny"></kat-icon>`
          : nothing;

        elements.push(html`
          <kat-option
            part="dropdown-option${index}"
            ?selected="${selected}"
            tabindex="-1"
            value="${value}"
            ?disabled=${disabled}
          >
            <div class="standard-option-content">
              <div class="standard-option-name">
                ${name}
              </div>
              <div class="standard-option-icon">
                ${iconElement}
              </div>
            </div>
          </div>
        `);

        return elements;
      }, []);

      this._allOptionsAreHidden = isSearchable && ret.length === 0;
      return ret;
    }

    // Return something falsey so that render() knows to not render the options slot (the default slot).
    return undefined;
  }

  renderNoOptionsMsg() {
    // This text needs to be localized
    return html`
      <kat-option disabled="true">
        <div class="standard-option-content">
          <div class="standard-option-name">
            ${this.getString('no_options')}
          </div>
        </div>
      </div>
    `;
  }

  renderSearchBar() {
    if (this.expanded) {
      const searchBar = html`
        <div class="search-wrapper">
          <input
            class="search-input"
            @input=${this.onInput}
            @keydown=${this.searchBarKeydown}
          >
          </input>
          <div class="search-icon">
            <kat-icon name="search" size="small"></kat-icon>
          </div>
        </div>
      `;
      return searchBar;
    }
    return undefined;
  }

  // Only for slots, and only if the list is searchable
  toggleElementVisibility() {
    let hasVisibleOptions = false;
    const elts = this.getElementsByTagName('kat-option');
    for (let eltIdx = 0; eltIdx < elts.length; eltIdx++) {
      if (
        this._searchTerm &&
        !elts[eltIdx].value
          .toLowerCase()
          .includes(this._searchTerm.toLowerCase())
      ) {
        elts[eltIdx].classList.add('search-term-hidden');
      } else {
        elts[eltIdx].classList.remove('search-term-hidden');
        hasVisibleOptions = true;
      }
    }

    this._allOptionsAreHidden = !hasVisibleOptions;
  }

  render() {
    let displayTextClass = 'header-row-text ';
    const valueOrPlaceholder = this.value ? 'value ' : 'placeholder ';
    displayTextClass += valueOrPlaceholder;

    let displayText = '';
    if (this.value) {
      if (this._hasSlottedOptions()) {
        const matching = Array.from(this.children).find(
          child => child.tagName === 'KAT-OPTION' && child.value === this.value
        );
        if (matching) {
          if (matching.label) {
            displayText = matching.label;
          } else {
            displayText = getContentOfFirstTextNode(matching);
          }
        }
      } else if (this.options) {
        const value = this.options.find(object => {
          return object.value === this.value;
        });
        if (value) {
          displayText = value.name;
        }
      }
    }

    if (!displayText && this.placeholder) {
      displayText = this.placeholder;
    }

    const formLabel = this.label
      ? html`
          <kat-label
            unique-id="form-label"
            class="form-label"
            part="dropdown-label"
            text="${this.label}"
            for=${this._uniqueId}
            .tooltipText=${ifNotNull(this.tooltipText)}
            .tooltipPosition=${ifNotNull(this.tooltipPosition)}
            .tooltipTriggerIcon=${ifNotNull(this.tooltipTriggerIcon)}
          ></kat-label>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <kat-label
            class="constraint-label"
            variant="constraint"
            emphasis="${ifNotNull(this.constraintEmphasis)}"
            text="${this.constraintLabel}"
            part="dropdown-constraint-label"
          ></kat-label>
        `
      : nothing;

    const stateLabel = this.stateLabel
      ? html`
          <kat-label
            class="state-label"
            variant="constraint"
            text="${this.stateLabel}"
            emphasis="${ifNotNull(this.stateEmphasis)}"
            state="${this.state}"
            part="dropdown-state-label"
          ></kat-label>
        `
      : nothing;

    // nothing is truthy so if we don't have JSON defined options we need to render an empty default
    // slot for the slotted content.
    const options = this._hasSlottedOptions() ? nothing : this.renderOptions();
    // We only want to render the default slot if there is either fallback content to render (JSON
    // case) or slotted content. As there is a bug with rendering empty fallback content in
    // polyfilled browsers.
    const defaultSlot = options ? html`<slot>${options}</slot>` : nothing;
    const searchBar = this.searchable ? this.renderSearchBar() : nothing;
    const noOptionMsg = this._allOptionsAreHidden
      ? this.renderNoOptionsMsg()
      : nothing;
    return html`
      ${formLabel}
      <div
        class="kat-select-container"
        aria-labelledby="form-label"
        aria-label=${ifNotNull(this.katAriaLabel)}
        role="listbox"
      >
        <div
          id=${this._uniqueId}
          class="select-header"
          part="dropdown-header"
          tabindex=${ifNotNull(this.disabled ? null : '0')}
          @click=${this.headerClick}
          @keydown=${this.headerKeydown}
        >
          <div class="header-row">
            <div class="${displayTextClass}">${displayText}</div>
            <div class="indicator">
              <kat-icon name="chevron-down" size="small"></kat-icon>
            </div>
          </div>
        </div>
        ${searchBar}
        <div
          class="select-options"
          part="dropdown-options"
          @selected=${this.selectOptionClick}
          @click=${this.selectOptionClick}
          @keydown=${this.selectOptionKeydown}
        >
          <div
            class="option-inner-container"
            style="${ifNotNull(
              this.maxHeight
                ? 'max-height:' + this.maxHeight + ';overflow:auto;'
                : null
            )}"
          >
            ${defaultSlot} ${noOptionMsg}
          </div>
        </div>
      </div>
      <div class="metadata">${constraintLabel} ${stateLabel}</div>
      <slot name="private-light-dom"></slot>
    `;
  }

  createEvents() {
    this.addEventListener('focusout', e => {
      const container = this.shadowRoot.querySelector('.kat-select-container');
      const newFocus = e.relatedTarget;
      if (
        !this.disabled &&
        !container.contains(newFocus) &&
        !this.contains(newFocus)
      ) {
        this.collapse();
      }
    });
  }

  async searchOptions() {
    const search = this._jumpToSearch.toLowerCase();
    const matching = this._getOptions().find(opt => {
      const text = getContentOfFirstTextNode(opt);
      return text && text.toLowerCase().startsWith(search) && !opt.disabled;
    });

    if (matching) {
      if (!this.expanded) {
        this.expanded = true;
        await this.updateComplete;
      }
      matching.focus();
    }
  }

  selectOption(element) {
    if (element.tagName !== 'KAT-OPTION') return;
    if (element.disabled) return;

    const initialValue = this.value;
    const newValue = element.value;
    if (initialValue !== newValue) {
      this.value = newValue;
      this.dispatchCustomEvent('change', { value: this.value });
    }

    this.collapseAndFocus();
  }

  async collapseAndFocus() {
    this.focus();
    this.collapse();
  }

  async collapse() {
    this.expanded = false;
    await this.updateComplete;
    this.dispatchCustomEvent('collapse', {}, false);

    // Next time it's opened, all items should be displayed
    if (this.searchable) {
      this._searchTerm = '';
    }
  }

  focusSearchBar() {
    // Automatically jump to input field
    const theInput = this.shadowRoot.querySelector('input');
    theInput.focus();
  }

  async toggleExpanded() {
    if (this.expanded) {
      this.collapse();
    } else {
      this.expanded = true;
      await this.updateComplete;
      if (this.searchable) {
        this.focusSearchBar();
      }
      this.dispatchCustomEvent('expand', {}, false);
    }
  }

  fitDropDownWindow() {
    const optionContainer = this.shadowRoot.querySelector('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );

    optionContainer.style.display = 'table';
    fitHandler(this, optionInnerContainer, !this.searchable);
    optionContainer.style.removeProperty('display');
  }

  setOrUnsetOptionsContainerBottomOffset() {
    const selectOptionsElement = this.shadowRoot.querySelector(
      '.select-options'
    );

    if (!selectOptionsElement) {
      return;
    }

    this.fitDropDownWindow();

    if (this.hasAttribute('drop-up')) {
      const offset = this.shadowRoot.querySelector('div.select-header')
        .offsetHeight;

      selectOptionsElement.setAttribute('style', `bottom: ${offset}px`);
    } else {
      selectOptionsElement.removeAttribute('style');
    }
  }

  selectOptionClick(event) {
    if (this.disabled) {
      return;
    }

    const el = getFirstMatchingParent(event.target, el =>
      el.hasAttribute('value')
    );
    if (el && el !== this) {
      this.selectOption(el);
    }
  }

  async searchBarKeydown(event) {
    const keyCode = event.keyCode || event.which;

    if (keyCode === Keys.ArrowDown) {
      event.preventDefault();
      this.focusInitialItem();
    } else if (keyCode === Keys.ArrowUp) {
      event.preventDefault();
      this.collapseAndFocus();
    }
  }

  selectOptionKeydown(event) {
    const target = event.target;
    const keyCode = event.keyCode || event.which;

    if (!this.disabled && target) {
      if (
        (keyCode === Keys.Space && !this._jumpToSearch) ||
        keyCode === Keys.Enter
      ) {
        event.preventDefault();
        this.selectOption(target);
      } else if (keyCode === Keys.ArrowUp) {
        //previous
        event.preventDefault();
        let prevEl = target.previousElementSibling;

        while (prevEl) {
          if (!prevEl.disabled) {
            prevEl.focus();
            return;
          }
          prevEl = prevEl.previousElementSibling;
        }
        // At the top of the list
        if (this.searchable) {
          this.focusSearchBar();
        } else {
          this.collapseAndFocus();
        }
      } else if (keyCode === Keys.ArrowDown) {
        //next
        event.preventDefault();
        let nextEl = target.nextElementSibling;
        while (nextEl) {
          if (!nextEl.disabled) {
            nextEl.focus();
            return;
          }
          nextEl = nextEl.nextElementSibling;
        }
      } else if (keyCode === Keys.Tab) {
        // Do nothing, let the browser do what it does best!
        return;
      } else if (keyCode === Keys.Escape) {
        this.collapseAndFocus();
      } else {
        this.jumpToSearch(keyCode);
      }
    }
  }

  // Select item in a newly-opened header
  focusInitialItem() {
    const options = this._getOptions();
    let selectedOption = options.find(element => element.selected);
    if (!selectedOption) {
      selectedOption = options.find(element => !element.disabled);
    }
    if (selectedOption) {
      selectedOption.focus();
    }
  }

  async headerKeydown(event) {
    const keyCode = event.keyCode || event.which;

    if (
      keyCode === Keys.ArrowDown ||
      keyCode === Keys.Space ||
      keyCode === Keys.Enter
    ) {
      event.preventDefault();
      this.expanded = true;
      await this.updateComplete;
      if (this.searchable) {
        this.focusSearchBar();
      } else {
        this.focusInitialItem();
      }
    } else {
      this.jumpToSearch(keyCode);
    }
  }

  headerClick(e) {
    const container = this.shadowRoot.querySelector('.kat-select-container');
    const newFocus = e.relatedTarget;
    if (!this.disabled && !container.contains(newFocus)) {
      this.toggleExpanded();
    }
  }

  async jumpToSearch(keyCode) {
    this._jumpToSearch += String.fromCharCode(keyCode);

    clearTimeout(this._timeoutId);
    await this.searchOptions();
    this._timeoutId = setTimeout(() => (this._jumpToSearch = ''), 300);
  }

  /**
   * Programmatically focus the header.
   * @katalmethod
   */
  focus() {
    const header = this.shadowRoot.querySelector('.select-header');
    if (header) {
      header.focus();
    }
  }

  _hasSlottedOptions() {
    return !!checkSlots(this).default;
  }

  _getOptions() {
    if (this._hasSlottedOptions()) {
      return Array.from(this.getElementsByTagName('kat-option'));
    }

    return Array.from(
      this.shadowRoot.querySelectorAll('.option-inner-container kat-option')
    );
  }

  // Pulled this directly from HMD- should be a utility function
  getString(stringId) {
    const locale = this.locale;
    let string = strings[stringId][locale];
    if (!string) {
      string = strings[stringId]['en-US'];
    }
    return string;
  }
}

KatalComponent.register('kat-dropdown', KatalDropdown);
